import { useState, useMemo, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { deriveTheme, THEMES } from '@ego/domain';
import applyTheme from '../../utils/applyTheme';
import { saveCustomTheme } from '../../utils/customThemes';
import { pushThemesToServer } from '../../utils/themeSync';
import useFocusTrap from '../../hooks/useFocusTrap';
import styles from './ThemeBuilderModal.module.css';

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;

/** Read a theme token off :root, falling back when it isn't a plain #rrggbb. */
function readTokenHex(token, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return HEX_PATTERN.test(value) ? value.toLowerCase() : fallback;
}

/**
 * Create/edit a custom theme from three seed colors (background, accent,
 * text). The lower pane is a live preview of the full derived palette —
 * mirrors iOS ThemeBuilderSheet. Saving persists the seeds, applies the
 * theme, and closes.
 */
export default function ThemeBuilderModal({ initial, onClose, onSaved }) {
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef);

  // Fresh builders start from the active theme's own seeds (iOS parity);
  // editing pre-fills the saved seeds. Dusk seeds are the fallback.
  const seeds = useMemo(() => ({
    bg: initial?.bgHex ?? readTokenHex('--bg-base', THEMES.dusk.bgBase),
    accent: initial?.accentHex ?? readTokenHex('--accent', THEMES.dusk.accent),
    text: initial?.textHex ?? readTokenHex('--text-primary', THEMES.dusk.textPrimary),
  }), [initial]);

  const [name, setName] = useState(initial?.name ?? 'My theme');
  const [bgHex, setBgHex] = useState(seeds.bg);
  const [accentHex, setAccentHex] = useState(seeds.accent);
  const [textHex, setTextHex] = useState(seeds.text);

  const trimmedName = name.trim();
  const previewName = trimmedName || 'My theme';
  const derived = deriveTheme(previewName, bgHex, accentHex, textHex);

  // Scoped CSS vars so the preview pane renders entirely in the derived
  // palette while the rest of the modal keeps the active theme.
  const previewVars = {
    '--bg-base': derived.bgBase,
    '--bg-surface': derived.bgSurface,
    '--bg-elevated': derived.bgElevated,
    '--bg-hover': derived.bgHover,
    '--text-primary': derived.textPrimary,
    '--text-muted': derived.textMuted,
    '--text-dim': derived.textDim,
    '--accent': derived.accent,
    '--border-accent': derived.borderAccent,
    '--ego-accent': derived.egoAccent,
    '--ego-border': derived.egoBorder,
    '--border-subtle': derived.borderSubtle,
    '--blue': derived.blue,
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trimmedName) return;
    const saved = saveCustomTheme({
      id: initial?.id,
      name: trimmedName,
      bgHex,
      accentHex,
      textHex,
    });
    applyTheme(`custom:${saved.id}`);
    pushThemesToServer();
    onSaved?.(saved);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Escape closes the builder no matter where focus sits. Capture-phase on
  // window so it runs (and stops) before window-level Escape listeners
  // (e.g. BlockDetailPage) fire from the same press.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  const colorRow = (label, value, setValue) => (
    <label className={styles.colorRow}>
      <span className={styles.colorRowLabel}>{label}</span>
      <input
        type="color"
        value={value}
        onChange={e => setValue(e.target.value)}
        className={styles.colorWell}
        aria-label={`${label} color`}
      />
    </label>
  );

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog} ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="theme-builder-heading">
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        <h2 id="theme-builder-heading" className={styles.heading}>
          {initial ? 'Edit theme' : 'Create theme'}
        </h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            Name
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={styles.input}
              placeholder="My theme"
              maxLength={40}
              autoFocus
              data-autofocus="true"
            />
          </label>
          <div className={styles.colorRows}>
            {colorRow('Background', bgHex, setBgHex)}
            {colorRow('Accent', accentHex, setAccentHex)}
            {colorRow('Text', textHex, setTextHex)}
          </div>
          <div className={styles.preview} style={previewVars} aria-hidden="true">
            <div className={styles.previewBreadcrumb}>{previewName}</div>
            <div className={styles.previewCard}>
              <span className={styles.previewCardTitle}>reference material</span>
              <span className={styles.previewCardMeta}>12 blocks · 2 days ago</span>
            </div>
            <p className={styles.previewBody}>
              Body copy sits in the muted tone on the base background, with dim metadata below.
            </p>
            <span className={styles.previewPill}>New channel +</span>
          </div>
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles.saveBtn} disabled={!trimmedName}>
              {initial ? 'Save changes' : 'Save theme'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
